import { useEffect } from 'react';
import { CARDS, MEANING_LANGS, cardPath, loadContent } from '../../data/cardMeanings';
import siteLanguages from '../../data/siteLanguages.json';
import { browserSiteLanguage } from '../../data/browserLanguage';

const STORAGE_KEY = 'nowtarot_picker_lang';

const PICKER_LANGS = new Set(siteLanguages.map((language) => language.code));

const MEANING_SECTION = {
  love: 'love',
  newLove: 'love',
  career: 'career',
  yesNo: 'yesNo',
};

const LOADERS = {
  es: () => import('../../data/pickerCopy/es.json'),
  hi: () => import('../../data/pickerCopy/hi.json'),
  zh: () => import('../../data/pickerCopy/zh.json'),
  ar: () => import('../../data/pickerCopy/ar.json'),
  fr: () => import('../../data/pickerCopy/fr.json'),
  pt: () => import('../../data/pickerCopy/pt.json'),
  de: () => import('../../data/pickerCopy/de.json'),
  ja: () => import('../../data/pickerCopy/ja.json'),
  ko: () => import('../../data/pickerCopy/ko.json'),
  it: () => import('../../data/pickerCopy/it.json'),
  tr: () => import('../../data/pickerCopy/tr.json'),
  id: () => import('../../data/pickerCopy/id.json'),
  pl: () => import('../../data/pickerCopy/pl.json'),
  vi: () => import('../../data/pickerCopy/vi.json'),
  th: () => import('../../data/pickerCopy/th.json'),
  nl: () => import('../../data/pickerCopy/nl.json'),
  ru: () => import('../../data/pickerCopy/ru.json'),
  fil: () => import('../../data/pickerCopy/fil.json'),
  uk: () => import('../../data/pickerCopy/uk.json'),
  ro: () => import('../../data/pickerCopy/ro.json'),
  sv: () => import('../../data/pickerCopy/sv.json'),
  cs: () => import('../../data/pickerCopy/cs.json'),
  el: () => import('../../data/pickerCopy/el.json'),
  hu: () => import('../../data/pickerCopy/hu.json'),
};

function detectedLang() {
  const code = browserSiteLanguage();
  return PICKER_LANGS.has(code) ? code : 'en';
}

function chosenLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || PICKER_LANGS.has(stored)) return stored;
  } catch (error) {
    return detectedLang();
  }
  return detectedLang();
}

function languageMeta(code) {
  return siteLanguages.find((language) => language.code === code);
}

function countLabel(copy, lang, count) {
  if (count <= 1) return copy.chrome.chooseOne;
  let text = copy.chrome.chooseMany.replace('{count}', count);
  const n10 = count % 10;
  const n100 = count % 100;
  const few = n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14);
  if (!few) return text;
  if (lang === 'ru') text = text.replace('карт', 'карты');
  else if (lang === 'uk') text = text.replace('карт', 'карти');
  else if (lang === 'pl') text = text.replace('kart', 'karty');
  else if (lang === 'cs') text = text.replace('tarotových karet', 'tarotové karty');
  return text;
}

function setText(element, text, lang) {
  if (!element || !text) return;
  element.textContent = text;
  element.lang = lang;
  if (lang === 'ar') element.setAttribute('dir', 'rtl');
}

function applyPageCopy(copy, pageId, lang) {
  const page = copy.pages[pageId];
  if (!page) return;
  setText(document.querySelector('.page-title'), page.heading, lang);
  const intro = document.querySelector('.intro-section p');
  setText(intro, page.intro, lang);
  const howHeading = document.querySelector('.intro-section h2');
  const howBody = document.querySelector('.intro-section h2 + p');
  if (page.how && howHeading && howBody) {
    setText(howHeading, copy.chrome.howHeading, lang);
    setText(howBody, page.how, lang);
  }
  setText(document.getElementById('deckInstruction'), page.instruction, lang);
  setText(document.getElementById('interpretBtn'), page.interpret, lang);
  const resultHeading = document.querySelector('#interpretationSection h3');
  setText(resultHeading, page.resultHeading, lang);
  setText(document.getElementById('newReadingBtn'), page.newReading, lang);
  const shuffle = document.getElementById('shuffle-again');
  setText(shuffle, copy.chrome.shuffleAgain, lang);
  const countNode = document.getElementById('cardsNeeded');
  if (countNode && window.tarotApp && window.tarotApp.currentSpread) {
    const count = window.tarotApp.currentSpread.cardCount;
    setText(
      countNode,
      countLabel(copy, lang, count),
      lang,
    );
  }
}

function mountSwitch(label, onClick) {
  const header = document.querySelector('.tarot-header');
  if (!header || document.getElementById('pickerLangSwitch')) return;
  const line = document.createElement('p');
  line.id = 'pickerLangSwitch';
  line.className = 'picker-lang';
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.addEventListener('click', onClick);
  line.appendChild(button);
  header.appendChild(line);
}

export default function usePickerLanguage(pageId) {
  useEffect(() => {
    const detected = detectedLang();
    const chosen = chosenLang();
    if ((chosen === 'en' && detected === 'en') || !LOADERS[chosen === 'en' ? detected : chosen]) {
      return undefined;
    }
    if (chosen === 'en') {
      const meta = languageMeta(detected);
      mountSwitch(meta ? meta.nativeLabel : detected, () => {
        try {
          localStorage.setItem(STORAGE_KEY, detected);
        } catch (error) {
          /* keep the English page if storage is blocked */
        }
        window.location.reload();
      });
      return undefined;
    }

    let cancelled = false;
    const meta = languageMeta(chosen);
    mountSwitch('English', () => {
      try {
        localStorage.setItem(STORAGE_KEY, 'en');
      } catch (error) {
        /* the next visit will follow the browser again */
      }
      window.location.reload();
    });

    LOADERS[chosen]().then(async (mod) => {
      if (cancelled) return;
      const copy = mod.default || mod;
      const picker = {
        lang: chosen,
        section: MEANING_SECTION[pageId] || 'general',
        copy,
        meanings: null,
        cardByName: Object.fromEntries(CARDS.map((card) => [card.names.en, card])),
        pathFor(card) {
          return cardPath(card, MEANING_LANGS.includes(chosen) ? chosen : 'en');
        },
      };
      window.NOW_TAROT_PICKER = picker;
      applyPageCopy(copy, pageId, chosen);
      if (meta) {
        const line = document.getElementById('pickerLangSwitch');
        if (line) line.lang = chosen;
      }
      if (MEANING_LANGS.includes(chosen)) {
        const meanings = await loadContent(chosen);
        if (!cancelled && window.NOW_TAROT_PICKER === picker) {
          picker.meanings = meanings;
        }
      }
    }).catch(() => {
      /* keep the English instructions if a translation file is missing */
    });

    return () => {
      cancelled = true;
    };
  }, [pageId]);
}
